// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## listSorter.js
//
// For interactives that involve matching columns (i.e. match the interval
// to the number of half steps in it) or sorting (sort the intervals from
// largest to smallest)

// import * as Sortable from 'sortablejs';
// import type DragonDrop from 'drag-on-drop';
import DragonDropConstructor from 'drag-on-drop';

import utils from '../utils';
import { colors } from 'design/src/tokens/colors';

/**
 * In px.
 */
const contentDivXMargin = 10;

/*
  lists can either be simple arrays of labels, or
  made up of objects:
    { label : text/html,
      classes : class(es) to apply,
      dataID : if different from label
      noSort : boolean, default false; keep in place.
    }
  */
interface ObjectListItem {
  /** To display in the list */
  label: string;
  /** Plaintext aria descriptor */
  ariaLabel?: string;
  /** css classes to apply to this item -- space delimited!!! e.g. "small highlighted" */
  classes?: string;
  /** if different from label  */
  dataID?: string;
  /** Keep item in place, don't let it be dragged */
  noSort?: boolean;
  /** CSS Styles to apply to this item only */
  styles?: {
    [cssStyleName: string]: string;
  };
  /** Whether to expand/shrink text to div size independently of other items */
  fitText?: boolean;
}

type ListItem = string | number | ObjectListItem;

export interface ListSorterOptions {
  /** If true, the first item of each list is locked in place at the top as a header */
  headerRow: boolean;
  /** If true, the first column is locked in place on the left as a header/fixed column */
  headerColumn: boolean;
  stripes: 'vertical' | 'horizontal';
  headerColumnStriping: boolean;
  headerRowStriping: boolean;
}

export type List = ListItem[];

export default class ListSorter {
  styles = {
    listColumn: {
      // width: '33%',
      overflow: 'hidden',
      // cssFloat: 'left',
      cursor: 'move',
      textAlign: 'center',
      // 'align-items': 'center',
    },
    noSortColumn: {
      // width: '33%',
      overflow: 'hidden',
      // cssFloat: 'left',
      cursor: 'no-drop',
      textAlign: 'center',
      // 'align-items': 'center',
    },
    headerItem: {
      //        height : "4vw",
      borderBottom: '1px solid black',
      fontSize: '2vw',
      // display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'no-drop',
    },
    listItem: {
      // display: 'flex',
      // alignItems: 'center',
      // justifyContent: 'center',
      //        height : "4vw",
      overflow: 'hidden',
      fontSize: '2vw',
      borderBottom: '1px solid black',
      borderLeft: '1px solid black',
      borderRight: '1px solid black',

      // boxShadow:
      //   'inset 0px -2px 2px rgba(0,0,0,0.8),' /* bottom internal shadow */ +
      //   'inset 0px 2px 2px rgba(255,255,255,0.8)' /* top internal highlight */,
    },
    paper: {
      backgroundColor: '#FFE883',
      borderBottom: '1px solid black',
    },
    ochre: {
      backgroundColor: 'purple',
    },
  };

  tableEl: HTMLTableElement;

  options: ListSorterOptions;

  private listRows: HTMLTableRowElement[]; // each list's div

  private lists: List[];

  // private sortables: Sortable[];
  private sortables: any[];

  private answerLists: List[];

  /** Items that need to be individually sized (shrunk/expanded) to fit their list item container */
  private sizeToFitItems: {
    cell: HTMLTableCellElement;
    contentDiv: HTMLDivElement;
    text: string;
    width: number;
    height: number;
  }[]; // for individually sized items

  /** All header-row items, if we're using a header row, so they're sized identically */
  private headerSizingItems: {
    cell: HTMLTableCellElement;
    contentDiv: HTMLDivElement;
    text: string;
  }[];

  /** Size items w/in each column the same way, if not sized individually already */
  private columnSizing: {
    cell: HTMLTableCellElement;
    contentDiv: HTMLDivElement;
    text: string;
  }[][];

  /** Whether to color incorrectly sorted items pink or not */
  private hinting: boolean = false;

  /**
   * Create a table for sorting
   * @param listDiv div in which to register sort table
   * @param lists list items can be made up of strings or objects
   * @param options
   */
  constructor(listDiv: HTMLDivElement, lists: List[], options: Partial<ListSorterOptions> = {}) {
    // @ts-expect-error
    window.sorter = this;
    this.options = utils.merge(
      {
        headerRow: true,
        headerColumn: true,
        stripes: 'horizontal',
        headerColumnStriping: true,
        headerRowStriping: false,
      },
      options,
    );

    this.makeGhostClass(listDiv);
    listDiv.classList.add('my-2');
    const tableEl = document.createElement('table');
    // If moved by keyboard, the hinting doesn't update, so...
    tableEl.addEventListener('keyup', () => {
      this.updateHint();
    });
    tableEl.classList.add('sortable-lists-table', 'border-2', 'card', 'mx-auto');
    this.tableEl = tableEl;
    this.listRows = []; // each list's div
    this.sortables = [];
    this.answerLists = [];
    this.lists = lists;

    // Responsive resizing:
    this.sizeToFitItems = []; // for individually sized items
    this.headerSizingItems = []; // size column headings the same
    this.columnSizing = []; // size items w/in columns the same, if not
    // sized individually already.
    // this.columnWidth = 100 / lists.length;
    // this.styles.listColumn.width = `${this.columnWidth}%`;
    // this.styles.noSortColumn.width = `${this.columnWidth}%`;
    // this.styles.listColumn['flex-direction'] = 'column';
    // this.styles.listColumn['display'] = 'flex';

    // Has to come after this.makeGhostClass because it wipes listDiv's innerHTML
    listDiv.appendChild(tableEl);

    const tbody = document.createElement('tbody');
    const width = (100 * ((this.lists.length - 1) / this.lists.length)).toFixed(2);
    tbody.style = `width: ${width}%;`;
    lists.forEach((list, i) => {
      // If we have a header column, make a header column,
      // otherwise, make all sortable columns
      if (this.options.headerColumn && i === 0) {
        this.makeHeaderRow(list);
      } else {
        if (i === 1) {
          // If this is the first row, we need a tbody element.
          this.tableEl.appendChild(tbody);
        }
        this.sortables.push(this.makeList(list, tbody));
      }
    });
  }

  resize(height: number) {
    this.tableEl.style.height = `${height}px`;
    // headerItem size = size / num rows
    // listItem
    const items1 = document.getElementsByClassName('nosort') as HTMLCollectionOf<HTMLElement>;
    const items2 = document.getElementsByClassName(
      'utheory-sortable-stripe',
    ) as HTMLCollectionOf<HTMLElement>;
    const items3 = document.getElementsByClassName(
      'utheory-sortable',
    ) as HTMLCollectionOf<HTMLElement>;

    const items = [...items1, ...items2, ...items3];

    const numItems = this.lists[0]?.length || 1;
    const itemHeight = ~~(height / numItems);
    items.forEach((item: HTMLElement) => {
      item.style.height = `${itemHeight}px`;
    });
    this.sizeToFitAsNeeded();
    this.sizeGroup(this.headerSizingItems);
    this.columnSizing.forEach((column) => {
      this.sizeGroup(column);
    }, this);
  }

  private sizeToFitAsNeeded() {
    this.sizeToFitItems.forEach((item) => {
      item.width = item.cell.clientWidth - 2 * contentDivXMargin;
      item.height = item.cell.clientHeight * 0.95;
      utils.fitText({ ...item, div: item.contentDiv }, true);
    });
  }

  private sizeGroup(
    group: {
      cell: HTMLTableCellElement;
      contentDiv: HTMLDivElement;
      text: string;
    }[],
  ) {
    let minFont = 200;
    group.forEach((item) => {
      const width = item.cell.clientWidth - 2 * contentDivXMargin;
      const height = item.cell.clientHeight * 0.95;
      minFont = Math.min(minFont, utils.fitText({ ...item, div: item.contentDiv, width, height }));
    });
    group.forEach((item) => {
      item.contentDiv.style.fontSize = '';
      item.cell.style.fontSize = `${minFont}rem`;
    });
  }

  private getArrays() {
    return this.sortables.map((sortable) =>
      sortable.items.map((div: HTMLDivElement) => div.dataset.id),
    );
  }

  /**
   * Check for correct or incorrect sorting
   * @param debugIsCorrect when provided, don't check, rather label all answers as correct
   * or incorrect based on this variable
   */
  check(debugIsCorrect?: boolean) {
    if (typeof debugIsCorrect !== 'undefined') {
      const numQ = this.answerLists.length * (this.answerLists[0]?.length || 0);
      return {
        right: debugIsCorrect ? numQ : 0,
        wrong: !debugIsCorrect ? numQ : 0,
      };
    }

    let wrongAnswers = 0;
    let rightAnswers = 0;
    const sortedLists = this.getArrays();
    for (let i = 0; i < sortedLists.length; i++) {
      const sortedList = sortedLists[i];
      const answerList = this.answerLists[i];
      for (let j = 0; j < sortedList.length; j++) {
        let answer = answerList![j];
        if (utils.isObject(answer) && typeof answer.dataID !== 'undefined') {
          answer = answer.dataID;
        } else if (utils.isObject(answer) && typeof answer.label !== 'undefined') {
          answer = answer.label;
        }
        // console.log(`Question ${i}-${j}. Expected: ${answer}. Got: ${sortedList[j]}`);

        if (sortedList[j] !== answer) {
          wrongAnswers++;
        } else {
          rightAnswers++;
        }
      }
    }
    return { wrong: wrongAnswers, right: rightAnswers };
  }

  hint() {
    this.hinting = true;
    const sortedLists = this.getArrays();

    for (let i = 0; i < sortedLists.length; i++) {
      const sortedList = sortedLists[i];
      const answerList = this.answerLists[i];
      const listCells = this.listRows[i]!.getElementsByTagName('td');
      console.log('row tds', listCells);
      for (let j = 0; j < sortedList.length; j++) {
        const col = this.options.headerRow ? j + 1 : j;
        let answer = answerList![j]!;
        if (utils.isObject(answer) && typeof answer.dataID !== 'undefined') {
          answer = answer.dataID;
        } else if (utils.isObject(answer) && typeof answer.label !== 'undefined') {
          answer = answer.label;
        }
        console.log(`Question ${i}-${j}. Expected: ${answer}. Got: ${sortedList[j]}`);

        // If it's wrong:
        if (sortedList[j] !== answer) {
          listCells[j]!.classList.add('utheory-sortable-hint');
          listCells[j]!.classList.remove('utheory-sortable-stripe');
          this.sortables[i].announcement('wrong', listCells[j]);
        } else {
          listCells[j]!.classList.remove('utheory-sortable-hint');
          if (this.options.stripes === 'horizontal') {
            listCells[j]!.classList.add('utheory-sortable-stripe');
          }
        }
      }
    }
  }

  updateHint() {
    if (this.hinting === true) this.hint();
  }

  private makeGhostClass(listDiv: HTMLDivElement) {
    let innerHTML = `
      <style>
        .sorter-content-div {
          margin: ${contentDivXMargin}px;
          align-self: center;
        }

        table.sortable-lists-table {
          width: 100%;
          display: flex;
          flex-wrap: nowrap;
        }

        .sortable-lists-table th {
          font-weight: 400;
        }

        .sortable-lists-table tbody,
        .sortable-lists-table thead {
          display: flex;
          flex-wrap: nowrap;
        }

        .sortable-lists-table tr {
          display: inline-flex;
          flex-direction: column;
          flex-grow: 1;
        }

        .sortable-lists-table td:focus-visible {
          outline: 4px inset rgb(15, 23, 42);
          box-shadow: inset rgb(15, 23, 42) 0px 0px 0px 2px, inset rgb(147, 197, 253) 0px 0px 0px 4px, rgba(0, 0, 0, 0.05) 0px 1px 2px 0px;
        }

        .sortable-lists-table td.pressed:focus-visible {
          background-color: ${colors.yellow[400]};
        }

        .noSort { background-color: white; }
    `;
    if (this.options.stripes === 'vertical')
      innerHTML += `
        .utheory-sortable-even { background-color: #8ADB88; }
        .utheory-sortable-odd { background-color: #7EBDFF; }
      `;

    if (this.options.stripes === 'horizontal')
      innerHTML += `
      .utheory-sortable-stripe:nth-child(odd) { background-color: #8ADB88; }
      .utheory-sortable-stripe:nth-child(even) {background-color: #7EBDFF; }
      `;

    innerHTML += `
      .utheory-sortable-hint { background-color: #FB7D9B; }
      .sortableGhostClass { opacity: 0.3 }`;

    innerHTML += `
      .gu-mirror {
        position: fixed !important;
        margin: 0 !important;
        z-index: 9999 !important;
        opacity: 0.8;
        -ms-filter: "progid:DXImageTransform.Microsoft.Alpha(Opacity=80)";
        filter: alpha(opacity=80);
      }
      .gu-hide {
        display: none !important;
      }
      .gu-unselectable {
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        user-select: none !important;
      }
      .gu-transit {
        opacity: 0.2;
        -ms-filter: "progid:DXImageTransform.Microsoft.Alpha(Opacity=20)";
        filter: alpha(opacity=20);
      }
    </style>`;
    innerHTML += `
    <div class="sr-only">
      <h4>Interaction Instructions</h4>
      <p>The table below contains cells which are intentionally out of order.
      Your job is to move them left or right until they match the column
      headers. To move a cell, focus on it, then press space to grab it. Use
      the right and left arrow keys to move it. When you're done moving it,
      press space to release it.</p>
    </div>
    `;

    listDiv.innerHTML = innerHTML;
  }

  /**
   * Makes a row for each sortable row.
   * @param list
   * @returns
   */
  private makeList(list: List, tbody: HTMLTableSectionElement) {
    console.log('making list', list);
    // Make the column:
    const rowEl = this.createRow({
      styleOptions: this.styles.listColumn,
      classNames: ['listColumn'],
    });
    const width = (100 * (1 / (this.lists.length - 1))).toFixed(2);
    rowEl.style.width = `${width}%`;
    console.log(`rowEl.style.width: ${rowEl.style.width}. Width: ${width}`);
    tbody.appendChild(rowEl);
    this.listRows.push(rowEl);

    // Scramble up the list:
    let scrambledList: List;
    let answerList: List;
    if (this.options.headerRow) {
      scrambledList = list.slice(0, 1);
      const scratchList = list.slice(1);
      answerList = list.slice(1);
      while (scratchList.length > 1 && utils.arrayCompare(answerList, scratchList)) {
        utils.shuffle(scratchList);
      }
      scrambledList = scrambledList.concat(scratchList);
    } else {
      scrambledList = list.slice(0);
      answerList = list.slice(0);
      while (
        answerList.length > 1 &&
        scrambledList.length > 1 &&
        utils.arrayCompare(answerList, scrambledList)
      ) {
        utils.shuffle(scrambledList);
      }
    }
    this.answerLists.push(answerList);

    // Any items not set to fitText will be grouped & sized to the same
    // font size:
    const sizeColumnTogether: {
      cell: HTMLTableCellElement;
      contentDiv: HTMLDivElement;
      text: string;
    }[] = [];

    // Populate the list:
    for (let i = 0; i < scrambledList.length; i++) {
      let sizingGroup = sizeColumnTogether;
      const item = scrambledList[i];
      // Decide if it's sortable & apply classes & styles:
      let itemIsSortable = i !== 0 || !this.options.headerColumn;
      if (!itemIsSortable) sizingGroup = this.headerSizingItems;

      if (utils.isObject(item) && typeof item.noSort !== 'undefined') {
        itemIsSortable = item.noSort;
      }

      // Switch even/odd style:
      const evenOdd = this.sortables.length % 2 === 0 ? 'even' : 'odd';

      // Apply the appropriate classes & styles based on this:
      let classes = itemIsSortable
        ? ['item', `utheory-sortable-${evenOdd}`, 'utheory-sortable-stripe']
        : ['nosort'];
      let style = itemIsSortable ? this.styles.listItem : this.styles.headerItem;
      // Apply any additional classes as requested:
      if (utils.isObject(item) && typeof item.classes !== 'undefined') {
        classes = classes.concat(item.classes.split(' '));
      }
      // Apply item-specific styles if desired:
      if (utils.isObject(item) && typeof item.styles !== 'undefined') {
        style = utils.merge({}, style);
        utils.merge(style, item.styles);
      }

      // Create the div:
      const itemCell = this.createCell({
        styleOptions: style,
        classNames: classes,
        isHeader: !itemIsSortable,
      });

      // Add its data-id attribute:
      if (utils.isObject(item) && typeof item.dataID !== 'undefined') {
        itemCell.setAttribute('data-id', item.dataID);
      } else if (utils.isObject(item) && typeof item.label !== 'undefined') {
        itemCell.setAttribute('data-id', item.label);
      } else {
        itemCell.setAttribute('data-id', `${item}`);
      }

      // Set its text:
      let itemText: string;
      if (utils.isObject(item) && typeof item.label !== 'undefined') {
        itemText = item.label;
      } else {
        itemText = `${item}`;
      }
      const contentDiv = document.createElement('div');
      contentDiv.classList.add('sorter-content-div');
      // contentDiv.style="margin: 15px;";

      itemCell.appendChild(contentDiv);
      contentDiv.innerHTML = itemText;
      rowEl.appendChild(itemCell);

      const ariaLabel =
        utils.isObject(item) && item.ariaLabel ? item.ariaLabel : itemCell.innerHTML;
      itemCell.dataset.ariaLabel = ariaLabel;

      // Size its text, if needed:
      if (utils.isObject(item) && item.fitText) {
        this.sizeToFitItems.push({
          cell: itemCell,
          contentDiv,
          text: itemText,
          width: rowEl.clientWidth - 2 * contentDivXMargin,
          height: contentDiv.clientHeight,
        });
      } else {
        sizingGroup.push({
          cell: itemCell,
          contentDiv,
          text: itemText,
        });
      }
    }

    this.columnSizing.push(sizeColumnTogether);

    const getPosition = (el: Element, allItems: Element[]) => {
      return allItems.indexOf(el) + 1;
    };

    const dragonDrop = new DragonDropConstructor(rowEl, {
      item: '.item',
      handle: false,
      // activeClass
      activeClass: 'pressed',
      // @ts-expect-error -- the type definition accidentally overwrites it's named
      // properties with its `{ [customEvt: string]: (el: HTMLElement) => string }`
      // declaration. Not worth the time to PR this upstream, since library is
      // unmaintained.
      announcement: {
        grabbed: (el, allItems) =>
          `${el.dataset.ariaLabel} grabbed (item #${getPosition(el, allItems)})`,
        dropped: (el, allItems) =>
          `${el.dataset.ariaLabel} dropped (item #${getPosition(el, allItems)})`,
        reorder: (el, allItems) =>
          `${el.dataset.ariaLabel} is now item ${allItems.indexOf(el) + 1}.`,
        wrong: (el) => `${el.dataset.ariaLabel} is wrong`,
      },
      dragulaOptions: {
        moves: (el?: Element) => !el?.classList.contains('nosort'),
        accepts: (el?: Element, target?: Element, source?: Element, sibling?: Element) =>
          !sibling?.classList.contains('nosort'),
      },
    });

    // Change the role of each cell to cell so we don't break
    // screan reader navigation:

    Array.from(this.tableEl.querySelectorAll('.item')).forEach((item) => {
      item.setAttribute('role', 'cell');
    });

    dragonDrop.dragula.on('drop', () => {
      this.updateHint();
      this.scrollable = true;
    });
    dragonDrop.dragula.on('drag', () => {
      this.scrollable = false;
    });

    document.addEventListener('touchmove', this.scrollListener, { passive: false });
    return dragonDrop;
  }

  private scrollable = true;

  private scrollListener = (evt: Event) => {
    if (this.scrollable) return;
    evt.preventDefault();
  };

  private makeHeaderRow(list: List) {
    const thead = document.createElement('thead');
    const width = (100 * (1 / this.lists.length)).toFixed(2);
    thead.style = `width: ${width}%;`;
    this.tableEl.appendChild(thead);
    const column = this.createRow({
      styleOptions: this.styles.noSortColumn,
      classNames: ['listHeaderColumn'],
    });
    column.style.width = '100%';
    thead.appendChild(column);
    const sizeColumnTogether: {
      cell: HTMLTableCellElement;
      contentDiv: HTMLDivElement;
      text: string;
    }[] = [];
    for (let i = 0; i < list.length; i++) {
      const item = list[i];
      const classes = ['nosort'];

      // Code below removed to avoid coloring left hand header in all situations -- to clarify
      // that it is not draggable. Frankly, I don't know why this was ever here... but
      // leaving it commented out in case this reveals a bug. -GCR (1/24)
      // if (this.options.headerColumnStriping && (i !== 0 || !this.options.headerRow)) {
      //   classes.push('utheory-sortable-stripe');
      // }

      const sizingGroup =
        i === 0 && this.options.headerRow ? this.headerSizingItems : sizeColumnTogether;

      if (utils.isObject(item) && item.classes) {
        classes.push(...item.classes.split(' '));
      }

      const itemCell = this.createCell({
        styleOptions: this.styles.headerItem,
        classNames: classes,
        isHeader: true,
      });

      // Set its text:
      const itemText = utils.isObject(item) && item.label ? item.label : `${item}`;
      const contentDiv = document.createElement('div');
      contentDiv.classList.add('sorter-content-div');
      contentDiv.innerHTML = itemText;
      itemCell.appendChild(contentDiv);
      column.appendChild(itemCell);

      // Size its text, if needed:
      if (utils.isObject(item) && item.fitText) {
        this.sizeToFitItems.push({
          cell: itemCell,
          contentDiv,
          text: itemText,
          width: itemCell.clientWidth - 2 * contentDivXMargin,
          height: itemCell.clientHeight,
        });
      } else {
        sizingGroup.push({
          cell: itemCell,
          contentDiv,
          text: itemText,
        });
      }
      column.appendChild(itemCell);
    }
    this.columnSizing.push(sizeColumnTogether);
  }

  private createRow(
    {
      styleOptions,
      classNames,
    }: {
      styleOptions: { [cssStyleName: string]: string };
      classNames: string[];
    }, // row: { type: 'head' | 'body' },
  ) {
    const tr = document.createElement('tr');
    utils.merge(tr.style, styleOptions);
    this.tableEl.appendChild(tr);

    if (typeof classNames !== 'undefined') {
      classNames.forEach((classToAdd) => {
        tr.classList.add(classToAdd);
      }, this);
    }
    return tr;
  }

  private createCell({
    styleOptions,
    classNames,
    isHeader,
  }: {
    styleOptions: { [cssStyleName: string]: string };
    classNames: string[];
    isHeader: boolean;
  }) {
    const cell = document.createElement(isHeader ? 'th' : 'td');
    if (!isHeader) {
      cell.setAttribute('role', 'cell');
    }
    const style = { ...styleOptions, display: 'flex', 'justify-content': 'center' };
    utils.merge(cell.style, style);
    if (typeof classNames !== 'undefined') {
      classNames.forEach((classToAdd) => {
        cell.classList.add(classToAdd);
      }, this);
    }
    return cell;
  }

  gameOver() {
    this.sortables.forEach((sortable) => {
      sortable.dragula.destroy();
    }, this);
    document.removeEventListener('touchmove', this.scrollListener);
  }
}
